export const MAX_PRESETS = 30
export const MAX_PRESET_NAME_LENGTH = 80

export type PresetPreferenceKey =
  | 'screener_display_presets'
  | 'screener_parse_presets'
  | 'screener_discovery_exchange_presets'
  | 'screener_discovery_presets'
  | 'screener_chart_presets'
  | 'screener_orderbook_presets'
  | 'coin_overview_presets'

export interface NamedPreset<T> {
  id: string
  name: string
  value: T
}

export function clonePresetValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/** Object key order is irrelevant; array order is meaningful for chart layouts. */
export function presetFingerprint(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      return Object.fromEntries(Object.entries(item).sort(([left], [right]) => left.localeCompare(right)))
    }
    return item
  })
}

export function readNamedPresets<T>(raw: unknown, normalize?: (value: unknown) => T | null): NamedPreset<T>[] {
  if (!Array.isArray(raw)) return []
  const ids = new Set<string>()
  const names = new Set<string>()
  return raw.slice(0, MAX_PRESETS).flatMap((item: unknown) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return []
    const entry = item as Record<string, unknown>
    if (typeof entry.id !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,79}$/.test(entry.id)
      || typeof entry.name !== 'string' || !entry.name.trim() || entry.name.length > MAX_PRESET_NAME_LENGTH
      || !Object.prototype.hasOwnProperty.call(entry, 'value')) return []
    const name = entry.name.trim()
    if (ids.has(entry.id.toLowerCase()) || names.has(name.toLowerCase())) return []
    try {
      const value = normalize ? normalize(entry.value) : entry.value as T
      if (value == null) return []
      ids.add(entry.id.toLowerCase())
      names.add(name.toLowerCase())
      return [{ id: entry.id, name, value: clonePresetValue(value) }]
    } catch { return [] }
  })
}
